<script setup lang="ts">
import { onMounted, watch, ref } from 'vue';
import { useRoute } from 'vue-router';
import sanitizeHtml from 'sanitize-html';

const route = useRoute();
const formRef = ref<HTMLFormElement | null>(null);

defineProps<{
  data?: {
    maichimp_action_for_footer_form?: string;
    title?: string;
    success_text?: string;
    column?: boolean;
  };
}>();

onMounted(() => {
  const script = document.createElement('script');
  script.src = '//s3.amazonaws.com/downloads.mailchimp.com/js/mc-validate.js';
  script.type = 'text/javascript';
  script.async = true;
  document.body.appendChild(script);
});

watch(
  () => route.fullPath,
  () => {
    if (formRef.value) {
      formRef.value.reset();

      const errorResponse = document.getElementById('mce-error-response');
      const successResponse = document.getElementById('mce-success-response');

      if (errorResponse) errorResponse.style.display = 'none';
      if (successResponse) successResponse.style.display = 'none';
    }
  }
);
</script>

<template>
  <div id="mc_embed_shell">
    <div id="mc_embed_signup">
      <div
        v-if="data?.title"
        class="typography mb-5"
        v-html="
          sanitizeHtml(data.title, {
            allowedAttributes: false,
            allowedTags: false,
            allowVulnerableTags: true,
            transformTags: {
              script: () => ({
                tagName: 'noscript',
                attribs: {},
              }),
            },
          })
        "
      />
      <form
        v-if="data?.maichimp_action_for_footer_form"
        id="mc-embedded-subscribe-form"
        ref="formRef"
        :action="data.maichimp_action_for_footer_form"
        method="post"
        name="mc-embedded-subscribe-form"
        class="validate"
        :class="data.column && 'form-normal'"
        target="_blank"
      >
        <div id="mc_embed_signup_scroll">
          <div class="indicates-required">
            <span class="asterisk">*</span>
            indicates required
          </div>
          <div
            :class="
              data.column
                ? 'flex flex-col gap-4'
                : 'gap-x-4 gap-y-8 lg:flex xl:gap-x-8'
            "
          >
            <div class="mc-field-group flex-1">
              <label :for="data.column ? 'mce-EMAILc' : 'mce-EMAIL'">
                Email Address
                <span class="asterisk">*</span>
              </label>
              <input
                :id="data.column ? 'mce-EMAILc' : 'mce-EMAIL'"
                type="email"
                name="EMAIL"
                class="required email"
                placeholder="Enter your email address"
                required
              />
            </div>
            <div class="optionalParent">
              <div class="clear foot">
                <ButtonLink type="submit" title="Subscribe" icon="arrow-r" />
              </div>
            </div>
          </div>
          <div aria-hidden="true" style="position: absolute; left: -5000px">
            <input
              type="text"
              name="b_e6d018e6ed296a6d24a45d635_97d395be13"
              tabindex="-1"
              value=""
              aria-label="Do not fill this field"
            />
          </div>
          <div id="mce-responses" class="clear foot">
            <div
              id="mce-error-response"
              class="response"
              style="display: none"
            />
            <div
              id="mce-success-response"
              class="response"
              style="display: none"
            />
          </div>
        </div>
      </form>
      <p
        v-if="data?.success_text"
        class="success-msg hidden pb-5.5 pt-1 text-xl text-current"
        v-html="
          sanitizeHtml(data.success_text, {
            allowedAttributes: false,
            allowedTags: false,
            allowVulnerableTags: true,
            transformTags: {
              script: () => ({
                tagName: 'noscript',
                attribs: {},
              }),
            },
          })
        "
      />
    </div>
  </div>
</template>

<style scoped>
:global(#mc_embed_signup) {
  @apply m-0 mb-10;
}

#mc_embed_signup {
  form {
    @apply m-0;
  }

  :global(.typography h5) {
    @apply mt-0 block font-serif text-3xl font-normal text-blue text-current;
  }

  :global(.typography p) {
    @apply mb-3;
  }

  .mc-field-group {
    @apply p-0;
  }

  .mc-field-group input {
    @apply h-[54px] w-full rounded-none border border-green bg-transparent p-2 px-3 indent-0 text-base text-green placeholder-green hover:border-blue hover:ring-1 hover:ring-blue focus:outline-none focus:ring-1 focus:ring-green md:h-14 md:px-5;
  }

  .form-normal .mc-field-group input {
    @apply border-current px-3 text-current placeholder-current;
  }

  .mc-field-group {
    @apply w-full;
  }

  .foot {
    @apply block w-auto pt-5 lg:pt-3;
  }

  div#mce-responses {
    @apply float-none m-0 p-0 text-s text-red;
  }

  :global(
    #mc-embedded-subscribe-form:has(#mce-error-response:not(:empty))
      .mc-field-group
      input
  ) {
    @apply !border-red !text-red;
  }

  div.response {
    @apply m-0 p-0 pt-2 font-normal text-red;
  }

  .mc-field-group label {
    @apply sr-only;
  }
}

:global(form:has(#mce-success-response[style*='block']) + .success-msg) {
  @apply block;
}
:global(form:has(#mce-success-response[style*='block'])) {
  @apply hidden;
}

:global(#mc_embed_signup form#mc-embedded-subscribe-form div.mce_inline_error) {
  @apply bg-transparent;
}

.indicates-required {
  @apply hidden;
}
</style>
